import { useEffect, useMemo, useState } from 'react'

import { selectAllVisibleAccounts } from '@/selectors/financeSelectors'
import { useAppDispatch } from '@/utils/typeHelpers'
import { useReselector } from '@/utils/sharedHooks'
import { fetchFinancialAccountsIfNeeded } from '@/actions/financialAccountActions'
import { fetchRolledOutInstitutionIds } from '@/actions/userActions'
import { getCurrentUser } from '@/selectors/user.selectors'
import { Loader } from '@/base'
import { Card, CardColors, Heading, HeadingVariants, Image, List } from '@/ui'
import {
  hasAutoStatementUploadAccount,
  hasHealthyManualStatementUploadAccount,
} from '../../helpers'

export const NoMoreStatementsCard = () => {
  const dispatch = useAppDispatch()
  const [fetching, setFetching] = useState(true)
  const user = useReselector(getCurrentUser)
  const financialAccounts = useReselector(selectAllVisibleAccounts)

  useEffect(() => {
    const fetch = async () => {
      await dispatch(fetchFinancialAccountsIfNeeded())
      if (user?.id) await dispatch(fetchRolledOutInstitutionIds(user.id, false))
      setFetching(false)
    }

    fetch()
  }, [dispatch, user?.id])

  const shouldShowCard = useMemo(
    () =>
      hasAutoStatementUploadAccount(financialAccounts) ||
      hasHealthyManualStatementUploadAccount(financialAccounts),
    [financialAccounts]
  )

  if (!shouldShowCard) {
    return null
  }

  if (fetching) {
    return <Loader />
  }

  return (
    <Card color={CardColors.LEVEL_1} className="flex flex-row">
      <Image
        src="https://heard-images.s3.amazonaws.com/assets/bank-auto.svg"
        alt="banner"
        height={140}
        width={140}
      />

      <div>
        <Heading variant={HeadingVariants.H2} className="mb-2">
          NEW! No more statement uploads for automated accounts
        </Heading>

        <div className="my-2">
          We can now automatically reconcile all accounts labeled as{' '}
          <strong>Automated</strong>. You won&apos;t need to manually upload
          statements for these accounts.
        </div>

        <List bulleted className="pl-7">
          <List.Item>
            You&apos;ll still need to upload statements for accounts labeled as{' '}
            <strong>Manual Upload</strong>.
          </List.Item>

          <List.Item>
            Automated accounts include those using automatic statement uploads
            and limited bank access. If you&apos;ve already set these up, you
            don&apos;t need to do anything else.
          </List.Item>
        </List>
      </div>
    </Card>
  )
}
